import { useState, useCallback } from 'react';
import api from '../services/api.service';

/**
 * useScanQueue - Shared hook for scan-driven workflows
 * Manages queue of scanned items with grouping and validation
 */
export default function useScanQueue() {
  const [queue, setQueue] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  /**
   * Add item to queue by scanning barcode or equipment_id
   */
  const addToQueue = useCallback(async (barcode) => {
    setLoading(true);
    setError(null);

    try {
      // Look up equipment by barcode or equipment_id
      const response = await api.get(`/equipment-items`, {
        params: { search: barcode, limit: 1 }
      });

      const items = response.data?.data || response.data || [];

      if (items.length === 0) {
        setError(`Equipment not found: ${barcode}`);
        return false;
      }

      const equipment = items[0];

      // Check if already in queue
      const alreadyInQueue = queue.some(item => item.id === equipment.id);
      if (alreadyInQueue) {
        setError(`Already in queue: ${equipment.equipment_id}`);
        return false;
      }

      // Add to queue
      setQueue(prev => [...prev, equipment]);
      return true;

    } catch (err) {
      console.error('Error looking up equipment:', err);
      setError(err.response?.data?.message || 'Failed to lookup equipment');
      return false;
    } finally {
      setLoading(false);
    }
  }, [queue]);

  /**
   * Remove item from queue
   */
  const removeFromQueue = useCallback((itemId) => {
    setQueue(prev => prev.filter(item => item.id !== itemId));
  }, []);

  /**
   * Clear entire queue
   */
  const clearQueue = useCallback(() => {
    setQueue([]);
    setError(null);
  }, []);

  /**
   * Group queue items by job
   * Returns: { jobId: { job_name, items: [...] }, ... }
   */
  const getGroupedByJob = useCallback(() => {
    const grouped = {};

    queue.forEach(item => {
      const jobId = item.current_job_id || 'unassigned';
      const jobName = item.job_name || 'Unassigned';

      if (!grouped[jobId]) {
        grouped[jobId] = {
          job_id: jobId,
          job_name: jobName,
          job_number: item.job_number || '',
          items: []
        };
      }

      grouped[jobId].items.push(item);
    });

    return grouped;
  }, [queue]);

  /**
   * Clear error message
   */
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  return {
    queue,
    loading,
    error,
    addToQueue,
    removeFromQueue,
    clearQueue,
    clearError,
    getGroupedByJob,
    queueCount: queue.length
  };
}
